(function () {
  'use strict';

  const theme = window.theme || {};
  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  const FOCUSABLE =
    'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), ' +
    'select:not([disabled]), textarea:not([disabled]), details > summary, [tabindex]:not([tabindex="-1"])';

  function debounce(fn, wait) {
    let t;
    return function () {
      clearTimeout(t);
      t = setTimeout(() => fn.apply(this, arguments), wait);
    };
  }

  function formatMoney(cents) {
    const format = theme.moneyFormat || '€{{amount}}';
    const amount = Number(cents) || 0;

    const group = (value, sep) => value.replace(/\B(?=(\d{3})+(?!\d))/g, sep);
    const parts = (decimals, thousandSep, decimalSep) => {
      const fixed = (amount / 100).toFixed(decimals);
      const [whole, frac] = fixed.split('.');
      return group(whole, thousandSep) + (frac ? decimalSep + frac : '');
    };

    const values = {
      amount: parts(2, ',', '.'),
      amount_no_decimals: parts(0, ',', '.'),
      amount_with_comma_separator: parts(2, '.', ','),
      amount_no_decimals_with_comma_separator: parts(0, '.', ','),
      amount_with_apostrophe_separator: parts(2, "'", '.'),
      amount_with_space_separator: parts(2, ' ', ','),
      amount_no_decimals_with_space_separator: parts(0, ' ', ',')
    };

    return format.replace(/\{\{\s*(\w+)\s*\}\}/g, (match, key) =>
      values[key] !== undefined ? values[key] : match
    );
  }
  window.MaisonFormatMoney = formatMoney;

  const ScrollLock = {
    count: 0,
    y: 0,
    lock() {
      if (this.count === 0) {
        this.y = window.scrollY;
        document.body.style.top = `-${this.y}px`;
        document.body.style.position = 'fixed';
        document.body.style.width = '100%';
        document.body.classList.add('is-scroll-locked');
      }
      this.count += 1;
    },
    unlock() {
      this.count = Math.max(0, this.count - 1);
      if (this.count === 0) {
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.width = '';
        document.body.classList.remove('is-scroll-locked');
        const root = document.documentElement;
        const behavior = root.style.scrollBehavior;
        root.style.scrollBehavior = 'auto';
        window.scrollTo(0, this.y);
        root.style.scrollBehavior = behavior;
      }
    }
  };

  /* Shopify's own messages ("sold out", "only 3 left") are worth showing; a
     programming error (a TypeError) is not. */
  const userMessage = (err) => {
    const name = err && err.name;
    if (name && name !== 'Error' && /Error$/.test(name)) return (window.theme && theme.strings && theme.strings.error) || 'Something went wrong';
    return (err && err.message) || (window.theme && theme.strings && theme.strings.error) || 'Something went wrong';
  };
  window.MaisonUserMessage = userMessage;

  const Toast = {
    el: null,
    timer: null,
    hideTimer: null,
    show(message, variant) {
      this.el = this.el || document.getElementById('Toast');
      if (!this.el) return;
      const msg = this.el.querySelector('.toast__message');
      if (msg) msg.textContent = message;
      this.el.classList.toggle('toast--error', variant === 'error');
      clearTimeout(this.timer);
      clearTimeout(this.hideTimer);
      this.el.hidden = false;
      requestAnimationFrame(() => this.el.classList.add('is-visible'));
      this.timer = setTimeout(() => {
        this.el.classList.remove('is-visible');
        this.hideTimer = setTimeout(() => { this.el.hidden = true; }, 500);
      }, 4000);
    }
  };

  function announce(message) {
    const region = document.getElementById('A11yStatus');
    if (!region) return;
    region.textContent = '';
    setTimeout(() => { region.textContent = message; }, 60);
  }

  function insideClosedDetails(el) {
    let details = el.closest('details:not([open])');
    while (details) {
      const summary = details.querySelector(':scope > summary');
      if (!summary || !summary.contains(el)) return true;
      details = details.parentElement && details.parentElement.closest('details:not([open])');
    }
    return false;
  }

  class Drawer extends HTMLElement {
    constructor() {
      super();
      this.opener = null;
      this.onKeydown = this.onKeydown.bind(this);
    }

    connectedCallback() {
      this.addEventListener('click', (e) => {
        if (e.target.closest('[data-drawer-close]')) {
          e.preventDefault();
          this.close();
        }
      });
    }

    open(opener) {
      if (this.classList.contains('is-open')) return;
      this.opener = opener || document.activeElement;
      this.classList.add('is-open');
      this.setAttribute('aria-hidden', 'false');
      ScrollLock.lock();
      if (window.theme && theme.phoneField) theme.phoneField.refresh();
      document.addEventListener('keydown', this.onKeydown);
      if (this.opener && this.opener.setAttribute) this.opener.setAttribute('aria-expanded', 'true');

      const panel = this.querySelector('.drawer__panel');
      const focusTarget =
        this.querySelector('[data-drawer-autofocus]') ||
        this.querySelector('.drawer__close') ||
        this.querySelector('input:not([type="hidden"])');
      const focus = () => focusTarget && focusTarget.focus();
      if (prefersReducedMotion.matches || !panel) focus();
      else panel.addEventListener('transitionend', focus, { once: true });
    }

    close() {
      if (!this.classList.contains('is-open')) return;
      this.classList.remove('is-open');
      this.setAttribute('aria-hidden', 'true');
      ScrollLock.unlock();
      document.removeEventListener('keydown', this.onKeydown);
      if (this.opener && this.opener.setAttribute) {
        this.opener.setAttribute('aria-expanded', 'false');
        if (document.body.contains(this.opener)) this.opener.focus();
      }
    }

    onKeydown(e) {
      if (e.key === 'Escape') {
        e.stopPropagation();
        this.close();
        return;
      }
      if (e.key !== 'Tab') return;

      const items = Array.from(this.querySelectorAll(FOCUSABLE)).filter((el) => {
        if (el.tabIndex < 0) return false;
        if (el.getAttribute('aria-hidden') === 'true') return false;
        if (el.closest('[aria-hidden="true"]')) return false;
        if (insideClosedDetails(el)) return false;
        return el.offsetParent !== null || el === document.activeElement;
      });
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
  }

  customElements.define('menu-drawer', class extends Drawer {});
  customElements.define('search-drawer', class extends Drawer {});

  /* A menu link to an anchor on the current page (e.g. the Vegan tab of the
     box page) only fires hashchange, so the drawer would otherwise stay open
     with the page locked behind it. */
  window.addEventListener('hashchange', () => {
    document.querySelectorAll('.drawer.is-open').forEach((d) => { if (typeof d.close === 'function') d.close(); });
  });

  document.addEventListener('click', (e) => {
    const trigger = e.target.closest('[data-drawer-open]');
    if (!trigger) return;
    const target = document.getElementById(trigger.dataset.drawerOpen);
    if (!target || typeof target.open !== 'function') return;
    e.preventDefault();
    target.open(trigger);
  });

  class StickyHeader extends HTMLElement {
    connectedCallback() {
      this.isOverlay = this.dataset.overlay === 'true';
      this.hideOnScroll = this.dataset.hideOnScroll === 'true';
      this.lastY = window.scrollY;
      this.onScroll = this.onScroll.bind(this);
      window.addEventListener('scroll', this.onScroll, { passive: true });
      this.onScroll();
      this.setupDropdowns();
    }

    disconnectedCallback() {
      window.removeEventListener('scroll', this.onScroll);
    }

    onScroll() {
      if (this.scrollFrame) return;
      this.scrollFrame = requestAnimationFrame(() => {
        this.scrollFrame = 0;
        const y = window.scrollY;
        const threshold = this.isOverlay ? window.innerHeight * 0.7 : 8;
        this.classList.toggle('is-stuck', y > threshold);
        if (this.hideOnScroll) {
          const goingDown = y > this.lastY && y > 200;
          this.style.transform = goingDown ? 'translateY(-100%)' : '';
        }
        this.lastY = y;
      });
    }

    setupDropdowns() {
      this.querySelectorAll('header-dropdown').forEach((dd) => {
        const trigger = dd.querySelector('[data-dropdown-trigger]');
        const panel = dd.querySelector('[data-dropdown-panel]');
        if (!trigger || !panel) return;

        let closeTimer;
        const show = () => {
          clearTimeout(closeTimer);
          panel.hidden = false;
          trigger.setAttribute('aria-expanded', 'true');
        };
        const hide = () => {
          closeTimer = setTimeout(() => {
            panel.hidden = true;
            trigger.setAttribute('aria-expanded', 'false');
          }, 120);
        };

        dd.addEventListener('mouseenter', show);
        dd.addEventListener('mouseleave', hide);
        dd.addEventListener('focusin', show);
        dd.addEventListener('focusout', (e) => {
          if (!dd.contains(e.relatedTarget)) hide();
        });
        dd.addEventListener('keydown', (e) => {
          if (e.key === 'Escape' && !panel.hidden) {
            trigger.focus();
            clearTimeout(closeTimer);
            panel.hidden = true;
            trigger.setAttribute('aria-expanded', 'false');
          }
        });
      });
    }
  }
  customElements.define('sticky-header', StickyHeader);

  const detailsKey = (d) => (d.closest('#CartDrawer') ? 'd|' : 'p|') + d.className.replace(/\bis-open\b/g, '').trim();

  const Cart = {
    seq: 0,
    rendered: 0,

    keepState() {
      const drawer = document.getElementById('CartDrawer');
      const body = drawer && drawer.querySelector('.drawer__body');
      const form = drawer && drawer.querySelector('.drawer__panel > form');
      const notes = Array.from(document.querySelectorAll('[data-cart-note]'));
      const active = notes.find((n) => n === document.activeElement);
      return {
        scroll: body ? body.scrollTop : 0,
        formScroll: form ? form.scrollTop : 0,
        open: Array.from(document.querySelectorAll('#CartDrawer details, [data-cart-page] details'))
          .map((d) => [detailsKey(d), d.open]),
        note: notes.length ? (active || notes[0]).value : null,
        phone: (document.querySelector('[data-cart-phone]') || {}).value,
        phoneCode: (document.querySelector('[data-cart-phone-code]') || {}).value,
        noteFocus: active
          ? { inDrawer: !!active.closest('#CartDrawer'), start: active.selectionStart, end: active.selectionEnd }
          : null
      };
    },

    restoreState(kept) {
      const drawer = document.getElementById('CartDrawer');
      const groups = {};
      kept.open.forEach(([key, open]) => { (groups[key] = groups[key] || []).push(open); });
      const seen = {};
      document.querySelectorAll('#CartDrawer details, [data-cart-page] details').forEach((d) => {
        const key = detailsKey(d);
        seen[key] = seen[key] === undefined ? 0 : seen[key] + 1;
        if (groups[key] && groups[key][seen[key]] !== undefined) {
          d.open = groups[key][seen[key]];
          d.classList.toggle('is-open', d.open);
        }
      });
      const body = drawer && drawer.querySelector('.drawer__body');
      const form = drawer && drawer.querySelector('.drawer__panel > form');
      if (body) body.scrollTop = kept.scroll;
      if (form) form.scrollTop = kept.formScroll;
      if (kept.phone) document.querySelectorAll('[data-cart-phone]').forEach((n) => { if (n.value !== kept.phone) n.value = kept.phone; });
      if (kept.phoneCode && !/^(\+|00)/.test((kept.phone || '').trim())) document.querySelectorAll('[data-cart-phone-code]').forEach((n) => { n.value = kept.phoneCode; });
      theme.phoneField.refresh();
      if (kept.note === null) return;
      document.querySelectorAll('[data-cart-note]').forEach((n) => { n.value = kept.note; });
      if (!kept.noteFocus) return;
      const scope = kept.noteFocus.inDrawer ? drawer : document.querySelector('[data-cart-page]');
      const note = scope && scope.querySelector('[data-cart-note]');
      if (!note) return;
      note.focus();
      try { note.setSelectionRange(kept.noteFocus.start, kept.noteFocus.end); } catch (e) {  }
    },

    captureFocus() {
      const el = document.activeElement;
      if (!el || el === document.body) return null;
      const line = el.closest('[data-line-key]');
      return {
        lineKey: line ? line.dataset.lineKey : null,
        role: el.dataset.qty ? `qty-${el.dataset.qty}`
          : el.hasAttribute('data-remove-line') ? 'remove'
          : el.hasAttribute('data-remove-box') ? 'remove-box'
          : el.classList.contains('qty__input') ? 'qty-input'
          : null,
        inDrawer: !!el.closest('#CartDrawer')
      };
    },

    restoreFocus(memo) {
      if (!memo || !memo.role) return;
      const scope = memo.inDrawer ? document.getElementById('CartDrawer') : document;
      if (!scope) return;
      let target = null;
      if (memo.lineKey) {
        const line = scope.querySelector(`[data-line-key="${CSS.escape(memo.lineKey)}"]`);
        if (line) {
          target = memo.role === 'remove' ? line.querySelector('[data-remove-line]')
            : memo.role === 'qty-input' ? line.querySelector('.qty__input')
            : line.querySelector(`[data-qty="${memo.role.replace('qty-', '')}"]`);
        }
      }
      if ((!target || target.disabled) && memo.lineKey) {
        const line = scope.querySelector(`[data-line-key="${CSS.escape(memo.lineKey)}"]`);
        if (line) {
          target = Array.from(line.querySelectorAll('button, a, input')).find((el) => !el.disabled);
        }
      }
      if (!target || target.disabled) {
        target = memo.inDrawer
          ? (scope.querySelector('.drawer__close') || scope.querySelector('a, button'))
          : (document.querySelector('[data-cart-page] h1') || document.querySelector('[data-cart-page] a, [data-cart-page] button'));
      }
      if (target) target.focus();
    },

    sectionIds() {
      const ids = ['cart-drawer'];
      const cartPage = document.querySelector('[data-cart-page]');
      if (cartPage && cartPage.dataset.sectionId) ids.push(cartPage.dataset.sectionId);
      return ids;
    },

    async refresh(openDrawer, sections, seq) {
      const memo = this.captureFocus();
      try {
        const cartPage = document.querySelector('[data-cart-page]');
        let data = sections;

        if (!data) {
          const url = `${window.location.pathname}?sections=${this.sectionIds().join(',')}`;
          const res = await fetch(url);
          if (!res.ok) throw new Error(`section render ${res.status}`);
          data = await res.json();
        }

        if (seq && seq < this.rendered) {
          /* A newer render already landed. Remember it, so the line queue can
             ask for one more render once its last change is in. */
          this.staleDropped = true;
          if (openDrawer) this.openIfRenderable();
          return;
        }
        if (seq) this.rendered = seq;

        const kept = this.keepState();
        let renderedDrawer = false;
        const drawer = document.getElementById('CartDrawer');
        if (data && data['cart-drawer'] && drawer) {
          const parsed = new DOMParser().parseFromString(data['cart-drawer'], 'text/html');
          const fresh = parsed.getElementById('CartDrawer');
          if (fresh) {
            drawer.innerHTML = fresh.innerHTML;
            renderedDrawer = true;
          }
        }

        if (cartPage && data[cartPage.dataset.sectionId]) {
          const parsed = new DOMParser().parseFromString(data[cartPage.dataset.sectionId], 'text/html');
          const fresh = parsed.querySelector('[data-cart-page]');
          if (fresh) cartPage.innerHTML = fresh.innerHTML;
        }
        this.restoreState(kept);

        const cart = await this.updateCount();

        if (!renderedDrawer) {
          console.warn('[cart] section render unavailable; rebuilding the drawer from /cart.js');
          this.renderFromJson(cart);
        }

        if (openDrawer) this.openIfRenderable();
        this.restoreFocus(memo);
      } catch (err) {
        console.error('[cart] refresh failed', err);
        try {
          const cart = await this.updateCount();
          this.renderFromJson(cart);
        } catch (e2) {  }
        if (openDrawer) this.openIfRenderable();
      }
    },

    openIfRenderable() {
      const drawer = document.getElementById('CartDrawer');
      if (drawer && drawer.querySelector('.drawer__panel')) return this.openDrawer();
      window.location.href = theme.routes.cart;
    },

    renderFromJson(cart) {
      const drawer = document.getElementById('CartDrawer');
      if (!drawer) return false;
      const body = drawer.querySelector('.drawer__body');
      if (!body) return false;

      const esc = (v) => String(v == null ? '' : v).replace(/[&<>"']/g, (c) =>
        ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

      if (!cart.items || !cart.items.length) {
        body.innerHTML = `<div class="cart-empty"><h3>${esc(theme.strings.cartEmpty || 'Your basket is empty')}</h3></div>`;
      } else {
        body.innerHTML = cart.items.map((item) => {
          const img = item.image ? `<img src="${esc(item.image)}" alt="" width="80" height="80" loading="lazy">` : '';
          const variant = item.variant_title ? `<p class="cart-line__variant">${esc(item.variant_title)}</p>` : '';
          return `<div class="cart-line" data-line-key="${esc(item.key)}">
              <span class="cart-line__media">${img}</span>
              <div>
                <h3 class="cart-line__title"><a href="${esc(item.url)}">${esc(item.product_title)}</a></h3>
                ${variant}
                <div class="cart-line__bottom">
                  <span class="cart-line__qty-static">&times;&nbsp;${Number(item.quantity) || 0}</span>
                  <span class="cart-line__price">${esc(formatMoney(item.final_line_price))}</span>
                </div>
                <button type="button" class="cart-line__remove" data-remove-line="${esc(item.key)}">
                  ${esc(theme.strings.remove || 'Remove')}
                </button>
              </div>
            </div>`;
        }).join('');
      }

      const total = drawer.querySelector('.cart-summary__row--total span:last-child');
      const vatRow = drawer.querySelector('.cart-summary__row--vat-added');
      if (vatRow) {
        /* As in Liquid: VAT on the taxable lines only (no gift cards). */
        const taxable = (cart.items || []).filter((i) => i.taxable !== false && !i.gift_card)
          .reduce((sum, i) => sum + (Number(i.final_line_price) || 0), 0);
        const vat = Math.round((taxable * (Number(vatRow.dataset.vatRate) || 0)) / 100);
        const sub = drawer.querySelector('.cart-summary__row--sub span:last-child');
        if (sub) sub.textContent = formatMoney(cart.total_price);
        vatRow.querySelector('span:last-child').textContent = formatMoney(vat);
        if (total) total.textContent = formatMoney(cart.total_price + vat);
      } else if (total) {
        total.textContent = formatMoney(cart.total_price);
      }
      const count = drawer.querySelector('.drawer__count');
      if (count) count.textContent = `(${cart.item_count})`;
      return true;
    },

    async updateCount() {
      const res = await fetch(`${theme.routes.cart}.js`);
      const cart = await res.json();
      document.querySelectorAll('span[data-cart-count]').forEach((el) => {
        el.textContent = cart.item_count;
        el.hidden = cart.item_count === 0;
      });
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart } }));
      return cart;
    },

    openDrawer() {
      const drawer = document.getElementById('CartDrawer');
      const opener = document.getElementById('CartButton');
      if (drawer && typeof drawer.open === 'function') drawer.open(opener);
    },

    async add(items, opts) {
      const options = opts || {};
      await NoteSaver.flush();
      const seq = ++this.seq;
      const body = {
        items: Array.isArray(items) ? items : [items],
        sections: this.sectionIds().join(','),
        sections_url: window.location.pathname
      };
      let res;
      try {
        res = await fetch(theme.routes.cart_add, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(body)
        });
      } catch (err) {
        throw new Error(theme.strings.network);
      }

      let data = {};
      try {
        data = await res.json();
      } catch (err) {
        throw new Error(theme.strings.error);
      }

      if (!res.ok) {
        throw new Error(data.description || data.message || theme.strings.error);
      }

      if (theme.cartType === 'page') {
        window.location.href = theme.routes.cart;
        return data;
      }

      await this.refresh(options.openDrawer !== false, data.sections, seq);
      return data;
    },

    async change(payload) {
      await NoteSaver.flush();
      const seq = ++this.seq;
      let res;
      try {
        res = await fetch(theme.routes.cart_change, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            ...payload,
            sections: this.sectionIds().join(','),
            sections_url: window.location.pathname
          })
        });
      } catch (err) {
        throw new Error(theme.strings.network);
      }
      let data = {};
      try {
        data = await res.json();
      } catch (err) {
        throw new Error(theme.strings.error);
      }
      if (!res.ok) throw new Error(data.description || data.message || theme.strings.error);
      await this.refresh(false, data.sections, seq);
      return data;
    },

    async removeBox(boxId) {
      await NoteSaver.flush();
      const seq = ++this.seq;
      let cart;
      try {
        const read = await fetch(`${theme.routes.cart}.js`);
        cart = await read.json();
      } catch (err) {
        throw new Error(theme.strings.network);
      }

      const updates = {};
      (cart.items || []).forEach((item) => {
        if (item.properties && item.properties._box_id === boxId) updates[item.key] = 0;
      });
      if (!Object.keys(updates).length) {
        await this.refresh(false, null, seq);
        return;
      }

      let res;
      try {
        res = await fetch(theme.routes.cart_update, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            updates,
            sections: this.sectionIds().join(','),
            sections_url: window.location.pathname
          })
        });
      } catch (err) {
        throw new Error(theme.strings.network);
      }
      if (!res.ok) throw new Error(theme.strings.error);
      let updated = null;
      try { updated = (await res.json()).sections; } catch (e) {  }
      await this.refresh(false, updated, seq);
    }
  };
  document.addEventListener(
    'submit',
    (e) => {
      const form = e.target;
      if (!form || !form.matches || !form.matches('form.account-form, form#ContactForm')) return;
      if (e.submitter && e.submitter.hasAttribute('formnovalidate')) return;
      if (form.checkValidity()) return;
      e.preventDefault();
      e.stopImmediatePropagation();
      form.reportValidity();
    },
    true
  );

  window.MaisonCart = Cart;
  window.MaisonToast = Toast;

  customElements.define('cart-drawer', class extends Drawer {});

  document.addEventListener('click', async (e) => {
    const removeLine = e.target.closest('[data-remove-line]');
    const removeBox = e.target.closest('[data-remove-box]');
    if (!removeLine && !removeBox) return;
    e.preventDefault();

    const line = e.target.closest('.cart-line, .cart-group');
    if (line && line.classList.contains('is-updating')) return;
    if (line) line.classList.add('is-updating');
    try {
      if (removeBox) await Cart.removeBox(removeBox.dataset.removeBox);
      else await Cart.change({ id: removeLine.dataset.removeLine, quantity: 0 });
      announce(theme.strings.removed);
    } catch (err) {
      Toast.show(userMessage(err), 'error');
      if (line) line.classList.remove('is-updating');
    }
  });

  const NoteSaver = {
    timer: null,
    pending: null,
    schedule(value) {
      this.pending = value;
      clearTimeout(this.timer);
      this.timer = setTimeout(() => this.flush(), 600);
    },
    flush(keepalive) {
      clearTimeout(this.timer);
      if (this.pending === null) return Promise.resolve();
      const note = this.pending;
      this.pending = null;
      return fetch(theme.routes.cart_update, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ note }),
        keepalive: Boolean(keepalive)
      }).catch(() => {});
    }
  };

  document.addEventListener('input', (e) => {
    const note = e.target.closest('[data-cart-note]');
    if (!note) return;

    document.querySelectorAll('[data-cart-note]').forEach((other) => {
      if (other !== note) other.value = note.value;
    });

    NoteSaver.schedule(note.value);
  });
  window.addEventListener('pagehide', () => NoteSaver.flush(true));

  /* Country-code phone fields (contact form, basket). The basket joins the picker
     and the number into one cart attribute, "+356 9916 4951"; a number typed with
     its own +/00 prefix wins over the picker. */
  theme.phoneField = {
    paint(select) {
      const field = select.closest('[data-phone-field]');
      const flag = field && field.querySelector('[data-phone-flag]');
      const opt = select.options[select.selectedIndex];
      const cc = opt && opt.dataset.cc;
      if (flag) {
        if (cc) flag.dataset.cc = cc; else delete flag.dataset.cc;
        flag.hidden = !cc;
      }
      if (select.matches('[data-cart-phone-code]')) this.fit(select);
    },
    /* The drawer is narrow: size the picker to the country shown, not the longest one. */
    fit(select) {
      const opt = select.options[select.selectedIndex];
      if (!opt || !select.offsetParent) return;
      const cs = getComputedStyle(select);
      this.ctx = this.ctx || document.createElement('canvas').getContext('2d');
      this.ctx.font = `${cs.fontStyle} ${cs.fontWeight} ${cs.fontSize} ${cs.fontFamily}`;
      const w = this.ctx.measureText(opt.text).width + parseFloat(cs.paddingLeft) + parseFloat(cs.paddingRight) + 6;
      select.style.width = `${Math.ceil(w)}px`;
    },
    full(field) {
      const num = (field.querySelector('[data-cart-phone]') || {}).value || '';
      const n = num.trim();
      if (!n) return '';
      if (/^(\+|00)/.test(n)) return n.replace(/^00/, '+');
      const code = (field.querySelector('[data-cart-phone-code]') || {}).value || '';
      return code ? `${code} ${n.replace(/^0+(?=\d)/, '')}` : n;
    },
    /* At checkout the joined number goes up as the attribute; the separate code is cleared. */
    prepareSubmit(form) {
      const phone = form && form.querySelector('[data-cart-phone]');
      const field = phone && phone.closest('[data-phone-field]');
      if (!field) return;
      const put = (name, value) => {
        let input = form.querySelector(`input[type="hidden"][name="${name}"]`);
        if (!input) {
          input = document.createElement('input');
          input.type = 'hidden';
          input.name = name;
          form.appendChild(input);
        }
        input.value = value;
      };
      put('attributes[Phone number]', this.full(field));
      put('attributes[Phone number code]', '');
      /* Baskets from before the rename (Oct 2026) still carry the old key;
         a blank value removes it, so an order never shows both. */
      put('attributes[Pick-up phone]', '');
      put('attributes[Pick-up phone code]', '');
      phone.removeAttribute('name');
      const code = field.querySelector('[data-cart-phone-code]');
      if (code) code.removeAttribute('name');
    },
    refresh() {
      document.querySelectorAll('[data-cart-phone-code]').forEach((select) => this.paint(select));
    }
  };
  theme.phoneField.refresh();
  if (document.fonts && document.fonts.ready) document.fonts.ready.then(() => theme.phoneField.refresh());

  document.addEventListener('input', (e) => {
    const phone = e.target.closest('[data-cart-phone]');
    if (!phone) return;
    phone.setCustomValidity('');
    document.querySelectorAll('[data-cart-phone]').forEach((other) => {
      if (other !== phone) other.value = phone.value;
    });
  });
  document.addEventListener('change', (e) => {
    const code = e.target.closest('[data-cart-phone-code]');
    if (code) {
      document.querySelectorAll('[data-cart-phone-code]').forEach((other) => {
        if (other === code) return;
        other.value = code.value;
        theme.phoneField.paint(other);
      });
    }
    const field = e.target.closest('[data-phone-field]');
    const phone = (code || e.target.closest('[data-cart-phone]')) && field && field.querySelector('[data-cart-phone]');
    if (!phone || !phone.value.trim() || !phone.checkValidity()) return;
    fetch(theme.routes.cart_update, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ attributes: { 'Phone number': theme.phoneField.full(field), 'Phone number code': '', 'Pick-up phone': '', 'Pick-up phone code': '' } })
    }).catch(() => {});
  });

  const phoneMissing = (form) => {
    const phone = form && form.querySelector('[data-cart-phone]');
    if (!phone) return false;
    phone.setCustomValidity('');
    if (phone.checkValidity()) return false;
    if (theme.strings.phoneInvalid) phone.setCustomValidity(phone.value.trim() ? '' : theme.strings.phoneInvalid);
    phone.reportValidity();
    phone.focus();
    return true;
  };
  document.addEventListener(
    'click',
    (e) => {
      const checkout = e.target.closest('[name="checkout"]');
      if (!checkout || !phoneMissing(checkout.form)) return;
      e.preventDefault();
      e.stopImmediatePropagation();
    },
    true
  );
  document.addEventListener(
    'submit',
    (e) => {
      const form = e.target;
      if (!form || !form.matches || !form.matches('#CartPageForm, #CartDrawerForm')) return;
      if (e.submitter && e.submitter.name !== 'checkout') return;
      if (!phoneMissing(form)) { theme.phoneField.prepareSubmit(form); return; }
      e.preventDefault();
      e.stopImmediatePropagation();
    },
    true
  );

  document.addEventListener('click', (e) => {
    const checkout = e.target.closest('[data-ordering-notice]');
    const modal = document.getElementById('OrderingNotice');
    if (!checkout || !modal || modal.dataset.acknowledged === 'true') return;
    e.preventDefault();
    modal.dataset.returnFocus = checkout.id || '';
    modal.__form = checkout.closest('form');
    modal.hidden = false;
    requestAnimationFrame(() => {
      const btn = modal.querySelector('[data-ordering-confirm]');
      if (btn) btn.focus();
    });
  });

  (function () {
    const modal = document.getElementById('OrderingNotice');
    if (!modal) return;

    const visible = (el) => el && document.contains(el) && el.offsetParent !== null;

    const closeModal = () => {
      modal.hidden = true;
      const back = modal.dataset.returnFocus && document.getElementById(modal.dataset.returnFocus);
      const candidates = [
        back,
        document.querySelector('#CartDrawer.is-open [data-ordering-notice]'),
        document.querySelector('[data-ordering-notice]')
      ];
      const target = candidates.find(visible);
      if (target) target.focus();
    };

    document.addEventListener('click', (e) => {
      if (modal.hidden) return;
      if (e.target.closest('[data-modal-close]')) {
        closeModal();
      } else if (e.target.closest('[data-ordering-confirm]')) {
        modal.dataset.acknowledged = 'true';
        modal.hidden = true;
        const stored = modal.__form && document.contains(modal.__form) ? modal.__form : null;
        const form =
          stored ||
          document.querySelector('#CartDrawer.is-open form') ||
          document.getElementById('CartPageForm') ||
          document.getElementById('CartDrawerForm');
        if (form) {
          const submitter = form.querySelector('[name="checkout"]');
          if (typeof form.requestSubmit === 'function') form.requestSubmit(submitter);
          else if (submitter) submitter.click();
          else form.submit();
        }
      }
    });

    document.addEventListener(
      'keydown',
      (e) => {
        if (modal.hidden) return;
        if (e.key === 'Escape') {
          e.preventDefault();
          e.stopPropagation();
          closeModal();
          return;
        }
        if (e.key !== 'Tab') return;
        const items = Array.from(modal.querySelectorAll(FOCUSABLE)).filter((el) => {
          if (el.tabIndex < 0) return false;
          if (el.getAttribute('aria-hidden') === 'true') return false;
          if (el.closest('[aria-hidden="true"]')) return false;
          return el.offsetParent !== null;
        });
        if (!items.length) return;
        const first = items[0];
        const last = items[items.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      },
      true
    );
  })();

  const lineQueue = new Map();

  async function sendLineQuantity(key, quantity) {
    const state = lineQueue.get(key) || { busy: false, queued: null };
    lineQueue.set(key, state);
    if (state.busy) {
      state.queued = quantity;
      return;
    }
    state.busy = true;
    try {
      await Cart.change({ id: key, quantity });
    } catch (err) {
      state.queued = null;
      Toast.show(userMessage(err), 'error');
      Cart.refresh(false, null, ++Cart.seq).catch(() => {});
    } finally {
      state.busy = false;
      const next = state.queued;
      state.queued = null;
      if (next !== null) sendLineQuantity(key, next);
      else {
        lineQueue.delete(key);
        /* The re-rendered line may still carry is-updating from a render that
           arrived while this change was in flight; and if this change's own
           response was dropped as stale, the drawer may not show it yet. */
        document.querySelectorAll(`[data-line-key="${CSS.escape(key)}"]`).forEach((el) => {
          const line = el.closest('.cart-line');
          if (line) line.classList.remove('is-updating');
        });
        if (Cart.staleDropped && !lineQueue.size) {
          Cart.staleDropped = false;
          Cart.refresh(false, null, ++Cart.seq).catch(() => {});
        }
      }
    }
  }

  class QuantityInput extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('.qty__input');
      if (!this.input) return;
      this.lineKey = this.dataset.lineKey;
      const pending = this.lineKey && lineQueue.get(this.lineKey);
      if (pending) {
        if (pending.queued !== null) this.input.value = pending.queued;
        const line = this.closest('.cart-line');
        if (line) line.classList.add('is-updating');
      }
      this.lastSent = Number(this.input.value);

      this.addEventListener('click', (e) => {
        const btn = e.target.closest('[data-qty]');
        if (!btn) return;
        e.preventDefault();
        const step = btn.dataset.qty === 'plus' ? 1 : -1;
        this.setValue(Number(this.input.value) + step);
      });

      this.input.addEventListener('change', () => this.setValue(Number(this.input.value)));
      this.input.addEventListener('keydown', (e) => {
        if (e.key !== 'Enter' || !this.lineKey) return;
        e.preventDefault();
        this.setValue(Number(this.input.value));
      });
    }

    keepFocusUsable(clamped, min, max) {
      const el = document.activeElement;
      if (!el || !this.contains(el) || !el.dataset.qty) return;
      const willDisable = el.dataset.qty === 'minus' ? clamped <= min : clamped >= max;
      if (!willDisable) return;
      const partner = this.querySelector(
        el.dataset.qty === 'minus' ? '[data-qty="plus"]' : '[data-qty="minus"]'
      );
      if (partner && !partner.disabled) partner.focus();
      else if (this.input) this.input.focus();
    }

    setValue(next) {
      const min = Number(this.input.min || 1);
      next = Math.floor(Number(next));
      if (!Number.isFinite(next)) next = min;
      const max = this.input.dataset.max ? Number(this.input.dataset.max) : Infinity;
      const clamped = Math.min(Math.max(next, min), max);

      if (next > max && max !== Infinity) {
        Toast.show((theme.strings.quantityError || 'Only [max] available').replace('[max]', max), 'error');
      }

      this.input.value = clamped;
      this.keepFocusUsable(clamped, min, max);
      this.querySelectorAll('[data-qty]').forEach((btn) => {
        btn.disabled = btn.dataset.qty === 'minus' ? clamped <= min : clamped >= max;
      });

      if (this.lineKey && clamped !== this.lastSent) {
        this.lastSent = clamped;
        const line = this.closest('.cart-line');
        if (line) line.classList.add('is-updating');
        sendLineQuantity(this.lineKey, clamped);
      }
    }
  }
  customElements.define('quantity-input', QuantityInput);

  class ProductForm extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      if (!this.form) return;
      this.button = this.form.querySelector('[name="add"]');
      this.form.addEventListener('submit', this.onSubmit.bind(this));
    }

    async onSubmit(e) {
      if (theme.cartType === 'page') return;
      e.preventDefault();
      if (this.button && this.button.getAttribute('aria-disabled') === 'true') return;
      if (this.form.dataset.unavailable === 'true') {
        Toast.show(theme.strings.unavailable || 'That combination is unavailable', 'error');
        return;
      }

      const formData = new FormData(this.form);
      const items = [
        {
          id: Number(formData.get('id')),
          quantity: Number(formData.get('quantity') || 1)
        }
      ];

      const sellingPlan = formData.get('selling_plan');
      if (sellingPlan) items[0].selling_plan = Number(sellingPlan);

      const properties = {};
      formData.forEach((value, key) => {
        const match = key.match(/^properties\[(.+)\]$/);
        if (match && String(value).trim() !== '') properties[match[1]] = value;
      });
      if (Object.keys(properties).length) items[0].properties = properties;

      this.setLoading(true);
      try {
        await Cart.add(items);
        Toast.show(theme.strings.addedToCart);
        announce(theme.strings.addedToCart);
      } catch (err) {
        Toast.show(userMessage(err), 'error');
      } finally {
        this.setLoading(false);
      }
    }

    setLoading(state) {
      if (!this.button) return;
      this.button.classList.toggle('is-loading', state);
      this.button.setAttribute('aria-disabled', String(state));
    }
  }
  customElements.define('product-form', ProductForm);

  class PredictiveSearch {
    constructor(root) {
      this.input = root.querySelector('[data-search-input]');
      this.results = root.querySelector('[data-predictive-results]');
      this.clear = root.querySelector('[data-search-clear]');
      if (!this.input || !this.results) return;

      this.controller = null;
      this.input.addEventListener('input', debounce(() => this.search(), 260));
      root.addEventListener('keydown', (e) => this.onKeydown(e));
      if (this.clear) {
        this.clear.addEventListener('click', () => {
          this.input.value = '';
          this.reset();
          this.input.focus();
        });
      }
    }

    reset() {
      if (this.controller) {
        this.controller.abort();
        this.controller = null;
      }
      this.results.innerHTML = '';
      this.input.setAttribute('aria-expanded', 'false');
      if (this.clear) this.clear.hidden = true;
    }

    async search() {
      const term = this.input.value.trim();
      if (this.clear) this.clear.hidden = term === '';
      if (term.length < 2) return this.reset();

      if (this.controller) this.controller.abort();
      this.controller = typeof AbortController === 'function' ? new AbortController() : null;

      const params = new URLSearchParams({
        q: term,
        'resources[type]': 'product,page,article',
        'resources[limit]': '6',
        section_id: 'predictive-search'
      });

      try {
        const res = await fetch(`${theme.routes.predictive_search}?${params}`, {
          signal: this.controller ? this.controller.signal : undefined
        });
        if (!res.ok) throw new Error('search failed');
        const html = await res.text();
        if (this.input.value.trim() !== term) return;
        const parsed = new DOMParser().parseFromString(html, 'text/html');
        const body = parsed.querySelector('.shopify-section');
        this.results.innerHTML = body ? body.innerHTML : '';
        this.input.setAttribute('aria-expanded', 'true');
        const count = this.results.querySelectorAll('[role="option"]').length;
        announce(count ? `${count} results` : 'No results');
      } catch (err) {
        if (err.name !== 'AbortError') this.reset();
      }
    }

    onKeydown(e) {
      if (e.key === 'Escape' && document.activeElement !== this.input) {
        this.input.focus();
        return;
      }
      if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(e.key)) return;
      if (e.key !== 'ArrowDown' && document.activeElement === this.input) return;
      const options = Array.from(this.results.querySelectorAll('[role="option"] a'));
      if (!options.length) return;
      e.preventDefault();
      const index = options.indexOf(document.activeElement);
      let next;
      if (e.key === 'Home') next = 0;
      else if (e.key === 'End') next = options.length - 1;
      else if (e.key === 'ArrowDown') next = Math.min(index + 1, options.length - 1);
      else next = Math.max(index - 1, -1);
      if (next === -1) this.input.focus();
      else options[next].focus();
    }
  }

  document.querySelectorAll('[data-search-form]').forEach((form) => new PredictiveSearch(form.parentElement));

  class AnnouncementBar extends HTMLElement {
    connectedCallback() {
      this.slides = Array.from(this.querySelectorAll('[data-announcement-slide]'));
      const speed = Number(this.dataset.autorotate);
      if (this.slides.length < 2 || !speed || prefersReducedMotion.matches) return;

      let i = 0;
      const start = () => {
        clearInterval(this.timer);
        this.timer = setInterval(() => {
          this.slides[i].hidden = true;
          i = (i + 1) % this.slides.length;
          this.slides[i].hidden = false;
        }, speed * 1000);
      };
      start();

      this.addEventListener('mouseenter', () => clearInterval(this.timer));
      this.addEventListener('mouseleave', start);
    }

    disconnectedCallback() {
      clearInterval(this.timer);
    }
  }
  customElements.define('announcement-bar', AnnouncementBar);

  const occasionSelect = document.getElementById('ContactOccasion');
  if (occasionSelect) {
    const wanted = (new URLSearchParams(location.search).get('occasion') || '').trim().toLowerCase();
    if (wanted) {
      const match = Array.from(occasionSelect.options).find((o) => o.value.toLowerCase().includes(wanted));
      if (match) occasionSelect.value = match.value;
    }
  }

  class QuoteCarousel extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-carousel-track]');
      this.slides = Array.from(this.querySelectorAll('[data-carousel-slide]'));
      this.controls = this.querySelector('[data-carousel-controls]');
      this.prev = this.querySelector('[data-carousel-prev]');
      this.next = this.querySelector('[data-carousel-next]');
      this.dots = this.querySelector('[data-carousel-dots]');
      this.count = this.querySelector('[data-carousel-count]');
      if (!this.track || !this.controls || this.slides.length < 2) return;

      const arrow = (btn, delta) => btn.addEventListener('click', () => {
        if (btn.getAttribute('aria-disabled') === 'true') return;
        this.go(this.page() + delta);
      });
      arrow(this.prev, -1);
      arrow(this.next, 1);
      this.dots.addEventListener('click', (e) => {
        const dot = e.target.closest('[data-carousel-page]');
        if (dot) this.go(Number(dot.dataset.carouselPage));
      });

      let raf = 0;
      this.track.addEventListener('scroll', () => {
        cancelAnimationFrame(raf);
        raf = requestAnimationFrame(() => this.sync());
      }, { passive: true });

      const relayout = () => { this.layout(); this.sync(); };
      if ('ResizeObserver' in window) {
        const observer = new ResizeObserver(relayout);
        observer.observe(this.track);
        this.slides.forEach((slide) => observer.observe(slide));
      } else {
        window.addEventListener('resize', relayout);
      }
      if (document.fonts && document.fonts.ready) document.fonts.ready.then(relayout);
      relayout();
    }

    perView() {
      const first = this.slides[0].getBoundingClientRect().width;
      if (!first) return 1;
      return Math.max(1, Math.round(this.track.clientWidth / first));
    }

    pages() { return Math.ceil(this.slides.length / this.perView()); }

    step() {
      const first = this.slides[0];
      const second = this.slides[1];
      return second ? second.offsetLeft - first.offsetLeft : first.offsetWidth;
    }

    maxLeft() { return Math.max(0, this.track.scrollWidth - this.track.clientWidth); }

    leftFor(n) { return Math.min(n * this.step() * this.perView(), this.maxLeft()); }

    page() {
      const step = this.step() * this.perView();
      if (!step) return 0;
      const last = this.pages() - 1;
      if (this.track.scrollLeft >= this.maxLeft() - 1) return last;
      return Math.min(Math.round(this.track.scrollLeft / step), last);
    }

    go(n) {
      const last = this.pages() - 1;
      const target = Math.min(Math.max(n, 0), last);
      this.track.scrollTo({ left: this.leftFor(target), behavior: prefersReducedMotion.matches ? 'auto' : 'smooth' });
    }

    layout() {
      const pages = this.pages();
      this.controls.hidden = pages < 2;
      if (pages < 2) return;
      if (this.dots.children.length !== pages) {
        this.dots.innerHTML = '';
        for (let i = 0; i < pages; i++) {
          const b = document.createElement('button');
          b.type = 'button';
          b.className = 'testimonials__dot';
          b.dataset.carouselPage = i;
          b.setAttribute('aria-label', `Reviews, page ${i + 1} of ${pages}`);
          this.dots.appendChild(b);
        }
      }
    }

    sync() {
      const current = this.page();
      const last = this.pages() - 1;
      this.prev.setAttribute('aria-disabled', String(current <= 0));
      this.next.setAttribute('aria-disabled', String(current >= last));
      Array.from(this.dots.children).forEach((dot, i) => {
        if (i === current) dot.setAttribute('aria-current', 'true');
        else dot.removeAttribute('aria-current');
      });
      if (this.count) this.count.textContent = `${current + 1} of ${last + 1}`;
      this.fitHeight();
    }

    fitHeight() {
      const r = this.track.getBoundingClientRect();
      let h = 0;
      this.slides.forEach((slide) => {
        const b = slide.getBoundingClientRect();
        const visible = b.right > r.left + 1 && b.left < r.right - 1;
        if (visible) h = Math.max(h, slide.offsetHeight);
      });
      if (h) this.track.style.height = `${h + 8}px`;
    }
  }
  customElements.define('quote-carousel', QuoteCarousel);

  if ('IntersectionObserver' in window && !prefersReducedMotion.matches) {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.05 }
    );
    let deepest = window.scrollY;
    let sweepPending = false;
    let listening = false;
    const queueSweep = () => {
      if (sweepPending) return;
      sweepPending = true;
      requestAnimationFrame(() => { sweepPending = false; sweep(); });
    };
    const listen = (on) => {
      if (on === listening) return;
      listening = on;
      const method = on ? 'addEventListener' : 'removeEventListener';
      window[method]('scroll', queueSweep, { passive: true });
      window[method]('resize', queueSweep, { passive: true });
    };
    /* Reads first, then writes, so a long page does not re-layout per section;
       and the scroll listeners go away once every section has been shown. */
    const sweep = () => {
      deepest = Math.max(deepest, window.scrollY);
      const limit = deepest - window.scrollY + window.innerHeight + 80;
      const pending = Array.from(document.querySelectorAll('.reveal:not(.is-visible)'));
      if (!pending.length) { listen(false); return; }
      const due = pending.filter((el) => el.getBoundingClientRect().top < limit);
      due.forEach((el) => { el.classList.add('is-visible'); observer.unobserve(el); });
      if (due.length === pending.length) listen(false);
    };
    const watch = () => {
      document.querySelectorAll('.reveal:not(.is-visible)').forEach((el) => observer.observe(el));
      listen(true);
      queueSweep();
    };
    watch();
    document.addEventListener('shopify:section:load', watch);
    window.addEventListener('load', sweep);
    sweep();
  } else {
    document.querySelectorAll('.reveal').forEach((el) => el.classList.add('is-visible'));
  }

  /* Cookie consent is Shopify's own banner (admin: Settings > Customer privacy
     > Cookie banner), which remembers the visitor's choice. The theme only adds
     a "Cookie preferences" link to the footer, shown where that banner applies,
     which reopens Shopify's preferences dialog. */
  const consentItems = document.querySelectorAll('[data-consent-open-item]');
  if (consentItems.length && window.Shopify && window.Shopify.loadFeatures) {
    window.Shopify.loadFeatures(
      [{ name: 'consent-tracking-api', version: '0.1' }],
      (error) => {
        const api = window.Shopify && window.Shopify.customerPrivacy;
        if (error || !api || !(api.shouldShowBanner && api.shouldShowBanner())) return;
        consentItems.forEach((li) => { li.hidden = false; });
        document.addEventListener('click', (e) => {
          if (!e.target.closest('[data-consent-open]')) return;
          if (window.privacyBanner && window.privacyBanner.showPreferences) window.privacyBanner.showPreferences();
        });
      }
    );
  }

  /* After a newsletter or contact form round-trip, Shopify sends the visitor
     back with ?customer_posted=true or ?contact_posted=true in the address,
     which is what renders the "Thank you" box. Left there, every refresh
     showed the box again. Once the page has loaded (so the #anchor scroll to
     the form has happened), drop the marker and the anchor from the address
     bar; a refresh then starts clean. The box itself stays until then. */
  const postedMarkers = ['customer_posted', 'contact_posted'];
  if (window.history && history.replaceState && postedMarkers.some((k) => new URLSearchParams(location.search).has(k))) {
    window.addEventListener('load', () => {
      try {
        const url = new URL(location.href);
        postedMarkers.forEach((k) => url.searchParams.delete(k));
        history.replaceState(history.state, '', url.pathname + url.search);
      } catch (e) { /* an odd address: leave it as it is */ }
    });
  }

  const promoteHoverImages = (root = document) => {
    root.querySelectorAll('img[data-srcset]').forEach((img) => {
      if (img.dataset.sizes) img.sizes = img.dataset.sizes;
      img.srcset = img.dataset.srcset;
      if (img.dataset.src) img.src = img.dataset.src;
      delete img.dataset.srcset;
      delete img.dataset.src;
      delete img.dataset.sizes;
    });
  };
  if (window.matchMedia && window.matchMedia('(hover: hover)').matches) {
    promoteHoverImages();
    document.addEventListener('shopify:section:load', (e) => promoteHoverImages(e.target));
    window.theme = window.theme || {};
    window.theme.promoteHoverImages = promoteHoverImages;
  }

  document.addEventListener('toggle', (e) => {
    if (e.target && e.target.tagName === 'DETAILS') e.target.classList.toggle('is-open', e.target.open);
  }, true);

  document.addEventListener('change', (e) => {
    const select = e.target.closest('[data-phone-field] select');
    if (select) theme.phoneField.paint(select);
  });

  /* The client asked that photos can't be saved from the site. Block the
     right-click menu and dragging on images (not in the theme editor, where
     merchants need them). It deters casual copying; screenshots stay possible. */
  if (!(window.Shopify && window.Shopify.designMode)) {
    const isMedia = (t) => t && t.closest && t.closest('img, picture, video, .hero__media');
    document.addEventListener('contextmenu', (e) => { if (isMedia(e.target)) e.preventDefault(); });
    document.addEventListener('dragstart', (e) => { if (isMedia(e.target)) e.preventDefault(); });
  }
})();
